import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Skeleton,
} from "antd";
import "./RoomBooking.css";
import Navbar from "../../../layouts/navbar";
import Footer from "../../../layouts/footer";
import Slider from "react-slick";
import {
  InfoCircleOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  TagsOutlined,
  DollarCircleOutlined,
  SolutionOutlined,
  WifiOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  LikeOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { AiOutlineWifi } from "react-icons/ai";
import {
  FaHotjar,
  FaRegSnowflake,
  FaShower,
  FaSmokingBan,
  FaTv,
  FaUtensilSpoon,
} from "react-icons/fa";
import { RiWindyFill } from "react-icons/ri";
import Lottie from "lottie-react-web";
import checkmark from "../../../checkmark.json";
import failed from "../../../failed.json";
import moment from "moment";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../layouts/auth/AuthContext";
import { useRoom, Room } from "./RoomContext";

// Import icons for amenities
import {
  FaSnowflake,
  FaLock,
  FaCoffee,
  FaPlug,
  // FaTelevision,
  FaWifi,
  FaWrench,
  FaTint,
  FaBan,
  FaArchive,
  FaCheckSquare,
  FaBell,
  FaWheelchair,
  FaHome,
  FaGlassMartini,
  FaNewspaper,
  FaPhone,
  FaBath,
} from "react-icons/fa";
import { useReferral } from "../../../utils/referral";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

// Types
interface RoomAmenity {
  roomfacilities: string;
  roomfacilities_pic: string;
}

interface RoomAddon {
  addon_desc: string;
  addon_price: string;
  addon_code: string;
}

interface SpecialRate {
  title: string;
  amount: string;
  id: string;
}

interface RatePlan {
  id: string;
  title: string;
  amount: string;
  refundable?: boolean;
  pay_later?: boolean;
  breakfast?: boolean;
  [key: string]: any;
}

interface RoomPrice {
  special?: SpecialRate[] | string;
  rateplan?: RatePlan[] | string;
  per_room_OriginalPrice: string | null;
  per_room_NonrefundablePrice: string | null;
  total_OriginalPrice: number;
  total_NonrefundablePrice: number;
  DiscountPrice: number;
  DiscountRate: string | number;
  DiscountType?: string;
}

// Use the Room interface from RoomContext and extend it if needed
// @ts-ignore
interface ExtendedRoom extends Room {
  RoomPrice: RoomPrice;
}

// Utility Functions
const totalDaysBetweenTwoDates = (
  startDate: string,
  endDate: string
): number => {
  const start = moment(startDate, "YYYY-MM-DD");
  const end = moment(endDate, "YYYY-MM-DD");
  return moment.duration(end.diff(start)).asDays();
};

const passed7Days = (date: string): boolean => {
  return moment(date).isAfter(moment().add(7, "days"));
};

// Icon mapping for amenities
const iconMap: { [key: string]: React.ComponentType<any> } = {
  "fa-snowflake-o": FaSnowflake,
  "fa-lock": FaLock,
  "fa-coffee": FaCoffee,
  "fa-plug": FaPlug,
  "fa-television": FaTv,
  "fa-wifi": FaWifi,
  "fa-wrench": FaWrench,
  "fa-tint": FaTint,
  "fa-ban": FaBan,
  "fa-archive": FaArchive,
  "fa-check-square-o": FaCheckSquare,
  "fa-bell-o": FaBell,
  "fa-wheelchair": FaWheelchair,
  "fa-home": FaHome,
  "fa-glass": FaGlassMartini,
  "fa-newspaper-o": FaNewspaper,
  "fa-phone": FaPhone,
  "fa-bath": FaBath,
  "fa-cutlery": FaUtensilSpoon,
};

// Custom Hooks
const useBookingState = () => {
  const { navigateWithReferral } = useReferral();

  const navProps = useLocation();

  const [initialLoading, setInitialLoading] = useState(true); // Add this
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState("success");
  const [rooms, setRooms] = useState<ExtendedRoom[]>([]);
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [adultPax, setAdultPax] = useState(2);
  const [childPax, setChildPax] = useState(0);
  const [roomQty, setRoomQty] = useState(1);
  const [searchDropdownVisible, setSearchDropdownVisible] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<ExtendedRoom | null>(null);
  const [selectedRate, setSelectedRate] = useState<{
    type: "standard" | "rateplan";
    rateplan?: RatePlan;
    specialRate?: SpecialRate;
  }>({ type: "standard" });
  const [searching, setSearching] = useState(false);
  const [payingNow, setPayingNow] = useState(false);
  const [loginAlertVisible, setLoginAlertVisible] = useState(false);
  const [filled, setFilled] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [priceType, setPriceType] = useState(0);
  const [specialRequest, setSpecialRequest] = useState("");
  const [checkedAddons, setCheckedAddons] = useState<string[]>([]);
  const [checkedAddonDetails, setCheckedAddonDetails] = useState<any[]>([]);
  const [checkAllAddonsChecked, setCheckAllAddonsChecked] = useState(false);
  const [visibleAmenitiesModal, setVisibleAmenitiesModal] = useState(false);
  const [visibleAddonModal, setVisibleAddonModal] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState<
    RoomAmenity[] | null
  >(null);
  const [openDatepicker, setOpenDatepicker] = useState(false);
  const [visibleDropdown, setVisibleDropdown] = useState(false);
  const [priceSummaryModalData, setPriceSummaryModalData] = useState<any>(null);
  const [priceSummaryModalVisible, setPriceSummaryModalVisible] =
    useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [emailLogin, setEmailLogin] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  return {
    initialLoading,
    setInitialLoading,
    navigateWithReferral,
    navProps,
    currentStep,
    setCurrentStep,
    paymentStatus,
    setPaymentStatus,
    rooms,
    setRooms,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    adultPax,
    setAdultPax,
    childPax,
    setChildPax,
    roomQty,
    setRoomQty,
    searchDropdownVisible,
    setSearchDropdownVisible,
    selectedRoom,
    setSelectedRoom,
    selectedRate,
    setSelectedRate,
    searching,
    setSearching,
    payingNow,
    setPayingNow,
    loginAlertVisible,
    setLoginAlertVisible,
    filled,
    setFilled,
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    priceType,
    setPriceType,
    specialRequest,
    setSpecialRequest,
    checkedAddons,
    setCheckedAddons,
    checkedAddonDetails,
    setCheckedAddonDetails,
    checkAllAddonsChecked,
    setCheckAllAddonsChecked,
    visibleAmenitiesModal,
    setVisibleAmenitiesModal,
    visibleAddonModal,
    setVisibleAddonModal,
    selectedAmenities,
    setSelectedAmenities,
    openDatepicker,
    setOpenDatepicker,
    visibleDropdown,
    setVisibleDropdown,
    priceSummaryModalData,
    setPriceSummaryModalData,
    priceSummaryModalVisible,
    setPriceSummaryModalVisible,
    loggingIn,
    setLoggingIn,
    emailLogin,
    setEmailLogin,
    password,
    setPassword,
    errorMessage,
    setErrorMessage,
  };
};

const useBookingActions = (state: ReturnType<typeof useBookingState>) => {
  const { navigateWithReferral } = useReferral();
  const { user, isLoggedIn, onOpenLoginModal } = useAuth();
  const {
    fetchRooms,
    createBooking,
    createPayment,
    currentRoomBookingDetails,
    onCurrentRoomBookingDetails,
  } = useRoom();

  const {
    initialLoading,
    setInitialLoading,
    searchDropdownVisible,
    setSearchDropdownVisible,
    currentStep,
    setCurrentStep,
    selectedRoom,
    setSelectedRoom,
    selectedRate,
    setSelectedRate,
    priceType,
    setPriceType,
    adultPax,
    setAdultPax,
    childPax,
    setChildPax,
    roomQty,
    setRoomQty,
    checkInDate,
    setCheckInDate,
    checkOutDate,
    setCheckOutDate,
    openDatepicker,
    setOpenDatepicker,
    visibleDropdown,
    setVisibleDropdown,
    checkedAddons,
    setCheckedAddons,
    checkedAddonDetails,
    setCheckedAddonDetails,
    checkAllAddonsChecked,
    setCheckAllAddonsChecked,
    filled,
    setFilled,
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    emailLogin,
    setEmailLogin,
    password,
    setPassword,
    errorMessage,
    setErrorMessage,
    loginAlertVisible,
    setLoginAlertVisible,
    loggingIn,
    setLoggingIn,
    payingNow,
    setPayingNow,
    searching,
    setSearching,
    rooms,
    setRooms,
    visibleAmenitiesModal,
    setVisibleAmenitiesModal,
    visibleAddonModal,
    setVisibleAddonModal,
    selectedAmenities,
    setSelectedAmenities,
    priceSummaryModalData,
    setPriceSummaryModalData,
    priceSummaryModalVisible,
    setPriceSummaryModalVisible,
    specialRequest,
    setSpecialRequest,
  } = state;

  // Step Management
  const onChangeStep = (index: number) => {
    setCurrentStep(index);
  };

  // Room Selection
  const onSelectRoom = (
    room: ExtendedRoom,
    priceType: number,
    _roomQty: number,
    _totalPax: number,
    rate: {
      type: "standard" | "rateplan";
      rateplan?: RatePlan;
      specialRate?: SpecialRate;
    }
  ) => {
    if (isLoggedIn) {
      const exceededOccupancy = _totalPax > room?.occupancy * _roomQty;
      if (exceededOccupancy) {
        alert(
          "We're sorry, the room quantity you have selected is unable to accommodate your total pax. Please increase your room quantity."
        );
        onVisibleDropdown();
        window.location.href = "#searchSection";
      } else {
        onCheckAddons([], []);
        setSelectedRoom(room);
        setSelectedRate(rate);
        setCurrentStep(2);
        setPriceType(priceType);
        onAutofillForm(true);

        onCurrentRoomBookingDetails({
          priceType,
          selectedRoom: room,
          selectedRate: rate,
          bookingParams: {
            roomQty,
            childPax,
            adultPax,
            checkInDate,
            checkOutDate,
            specialRequest,
            name,
            email,
            phone,
          },
        });
        navigateWithReferral("checkout");
      }
    } else {
      setSelectedRoom(room);
      setSelectedRate(rate);
      onOpenLoginModal();
      setPriceType(priceType);
    }
  };

  // Modal Management
  const onOpenPriceSummaryModal = (item: any) => {
    setPriceSummaryModalData(item);
    setPriceSummaryModalVisible(true);
  };

  const onShowAllAmenities = (amenities: RoomAmenity[]) => {
    setVisibleAmenitiesModal(true);
    setSelectedAmenities(amenities);
  };

  // UI State Management
  const onVisibleDropdown = () => {
    setVisibleDropdown(!visibleDropdown);
  };

  const onOpenChange = () => {
    setOpenDatepicker(!openDatepicker);
  };

  const onOpenDatepicker = () => {
    setOpenDatepicker(true);
  };

  // Input Handlers
  const onChangeInput = (type: string, value: string) => {
    const setters: {
      [key: string]: React.Dispatch<React.SetStateAction<string>>;
    } = {
      name: setName,
      email: setEmail,
      phone: setPhone,
      specialRequest: setSpecialRequest,
      emailLogin: setEmailLogin,
      password: setPassword,
    };

    if (setters[type]) {
      setters[type](value);
    }
  };

  const onChangeDate = (date: string[]) => {
    setCheckInDate(date[0]);
    setCheckOutDate(date[1]);
    setOpenDatepicker(false);
  };

  const onHandleVisibleChange = (flag: boolean) => {
    setSearchDropdownVisible(flag);
  };

  // Quantity Management
  const onChangeQuantity = (type: string, action: string) => {
    const limits: {
      [key: string]: {
        min: number;
        max: number;
        setter: React.Dispatch<React.SetStateAction<number>>;
        current: number;
      };
    } = {
      adultPax: { min: 0, max: 20, setter: setAdultPax, current: adultPax },
      childPax: { min: 0, max: 20, setter: setChildPax, current: childPax },
      roomQty: { min: 0, max: 20, setter: setRoomQty, current: roomQty },
    };

    const config = limits[type];
    if (!config) return;

    let newValue = config.current;

    if (action === "minus" && newValue > config.min) {
      newValue--;
    } else if (action === "add" && newValue < config.max) {
      newValue++;
    }

    config.setter(newValue);
  };

  // Addon Management
  const onChangeAddonInput = (addonCode: string, type: string, value: any) => {
    const newDetails = [...checkedAddonDetails];
    const index = newDetails.findIndex(
      (item: any) => item.addon_code === addonCode
    );

    if (index !== -1) {
      newDetails[index][type] = value;
    } else {
      newDetails.push({ addon_code: addonCode, [type]: value });
    }

    setCheckedAddonDetails(newDetails);
  };

  const onCheckAddons = (
    checkedValues: string[],
    allOptionValues: string[]
  ) => {
    const allChecked =
      checkedValues.length > 0 &&
      checkedValues.length === allOptionValues.length;
    setCheckAllAddonsChecked(allChecked);
    setCheckedAddons(checkedValues);

    let details = [...checkedAddonDetails];
    checkedValues.forEach((code) => {
      if (!details.find((i: any) => i.addon_code === code)) {
        details.push({ addon_code: code, night: 1, pax: 1 });
      }
    });

    const filteredDetails = details.filter((i: any) =>
      checkedValues.includes(i.addon_code)
    );
    setCheckedAddonDetails(filteredDetails);
  };

  const onCheckAllAddons = (checked: boolean, allOptionValues: string[]) => {
    setCheckAllAddonsChecked(checked);

    if (checked) {
      const details: any[] = [];
      allOptionValues.forEach((code) => {
        details.push({ addon_code: code, night: 1, pax: 1 });
      });
      setCheckedAddonDetails(details);
      setCheckedAddons(allOptionValues);
    } else {
      setCheckedAddonDetails([]);
      setCheckedAddons([]);
    }
  };

  // Form Management
  const onAutofillForm = (checked: boolean) => {
    setFilled(checked);
    if (checked && user) {
      setName(`${user.firstname || ""} ${user.lastname || ""}`.trim());
      setEmail(user.email || "");
      setPhone(user.phone || "");
    } else {
      setName("");
      setEmail("");
      setPhone("");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const checkin = params.get("checkin");
    const checkout = params.get("checkout");
    const adult = params.get("adult");
    const child = params.get("child");
    const rooms = params.get("rooms");

    if (checkin) setCheckInDate(checkin);
    if (checkout) setCheckOutDate(checkout);
    if (adult) setAdultPax(Number(adult));
    if (child) setChildPax(Number(child));
    if (rooms) setRoomQty(Number(rooms));

    // optional: auto-trigger search if params exist
    if (checkin && checkout) {
      setInitialLoading(true); // Start loading
      fetchRooms({
        checkin_date: checkin,
        checkout_date: checkout,
        adult_pax: adult ? Number(adult) : 1,
        child_pax: child ? Number(child) : 0,
        room_qty: rooms ? Number(rooms) : 1,
      })
        .then((response: any) => {
          setRooms(response);
        })
        .catch(() => setRooms([]))
        .finally(() => {
          setInitialLoading(false); // End loading regardless of result
        });
    } else {
      setInitialLoading(false); // No auto-search, hide loading
    }
  }, []);

  // API Actions
  const onSearch = () => {
    if (!checkInDate && !checkOutDate) {
      onOpenDatepicker();
      return;
    }

    alert(
      "Dear valued guest, please note that our reservation system can only acknowledge selection of only one room type for each reservation. Kindly create a new reservation if you wish to book for another room type. Thank you."
    );

    // Update URL params
    const params = new URLSearchParams();
    params.set("checkin", checkInDate || "");
    params.set("checkout", checkOutDate || "");
    params.set("adult", adultPax.toString());
    params.set("child", childPax.toString());
    params.set("rooms", roomQty.toString());

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`
    );

    // Do the fetch
    setSearching(true);

    fetchRooms({
      checkin_date: checkInDate,
      checkout_date: checkOutDate,
      adult_pax: adultPax,
      child_pax: childPax,
      room_qty: roomQty,
    })
      .then((response: any) => {
        setSearching(false);
        setRooms(response);
      })
      .catch((error) => {
        setSearching(false);
        setRooms([]);
        alert(
          "No Rooms available on your selected dates. Please try another dates."
        );
        console.error(error);
      });
  };

  const onSubmit = (checkoutParams: any) => {
    console.log("checkoutParams ====> ", checkoutParams);
  };

  const onPayNow = () => {
    setPayingNow(true);
    const params = {
      UUID: user?.UUID,
      RoomId: selectedRoom?.RoomId,
      Price_type: priceType,
      checkin_date: checkInDate,
      checkout_date: checkOutDate,
      adult_pax: adultPax,
      child_pax: childPax,
      room_qty: roomQty,
      Booking_Name: name,
      Booking_Email: email,
      Booking_Phone: phone,
      SpecialRequest: specialRequest,
      rateplan_id:
        selectedRate?.type === "rateplan" ? selectedRate?.rateplan?.id : null,
      special_rate_id: selectedRate?.specialRate
        ? selectedRate?.specialRate?.id
        : null,
      rateplan_amount: selectedRate?.specialRate
        ? parseFloat(selectedRate.specialRate.amount).toFixed(2)
        : selectedRate?.rateplan
        ? parseFloat(selectedRate.rateplan.amount).toFixed(2)
        : undefined,
    };

    const addonParams = [...checkedAddonDetails];

    createBooking(params, addonParams)
      .then((response) => {
        return createPayment({
          UUID: user?.UUID,
          order_number: response.order_number,
          paytype: 0,
        });
      })
      .then((response) => {
        window.location.replace(response);
      })
      .catch((error) => {
        console.error("Payment error:", error);
        setPayingNow(false);
      });
  };

  return {
    onChangeStep,
    onSelectRoom,
    onOpenPriceSummaryModal,
    onShowAllAmenities,
    onVisibleDropdown,
    onOpenChange,
    onOpenDatepicker,
    onChangeInput,
    onChangeDate,
    onHandleVisibleChange,
    onChangeQuantity,
    onChangeAddonInput,
    onCheckAddons,
    onCheckAllAddons,
    onAutofillForm,
    onSearch,
    onPayNow,
    onSubmit,
  };
};

// Sub Components
const SearchSection = ({
  state,
  actions,
}: {
  state: ReturnType<typeof useBookingState>;
  actions: ReturnType<typeof useBookingActions>;
}) => {
  const {
    checkInDate,
    checkOutDate,
    adultPax,
    childPax,
    roomQty,
    openDatepicker,
    visibleDropdown,
    searching,
  } = state;
  const {
    onChangeDate,
    onOpenChange,
    onVisibleDropdown,
    onChangeQuantity,
    onSearch,
  } = actions;

  const menu = (
    <Menu style={{ padding: "1em", borderRadius: "6px" }}>
      {["adultPax", "childPax", "roomQty"].map((type) => (
        <>
          <div
            key={type}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1em",
            }}
          >
            <div>
              <Text style={{ fontSize: 16, color: "#1a1a1a" }}>
                {type === "adultPax"
                  ? "Adult"
                  : type === "childPax"
                  ? "Child"
                  : "Room"}
              </Text>
              <div>
                {type === "childPax" && (
                  <Text style={{ fontSize: 12, marginTop: -20 }}>
                    Age 4-11 Years Old
                  </Text>
                )}
              </div>
            </div>

            <div>
              <Button
                type="link"
                style={{
                  fontSize: 20,
                  marginRight: "1em",
                  marginTop: 0,
                  padding: "0em",
                  color: "#e90056",
                }}
                onClick={() => onChangeQuantity(type, "minus")}
              >
                -
              </Button>
              <Text style={{ fontSize: 16, color: "#1a1a1a" }}>
                <b>{state[type as keyof typeof state] as number}</b>
              </Text>
              <Button
                type="link"
                style={{
                  fontSize: 20,
                  marginLeft: "1em",
                  marginTop: 0,
                  padding: "0em",
                  color: "#e90056",
                }}
                onClick={() => onChangeQuantity(type, "add")}
              >
                +
              </Button>
            </div>
          </div>
        </>
      ))}
    </Menu>
  );

  return (
    <div id="searchSection">
      <Title level={4} style={{ color: "#1a1a1a", marginBottom: 16 }}>
        Find best deals for your stay!
      </Title>
      <div className="searchWrapper">
        <RangePicker
          open={openDatepicker}
          onOpenChange={onOpenChange}
          className="rangePickerSearch"
          size="large"
          placeholder={["Check in", "Check out"]}
          value={
            checkInDate && checkOutDate
              ? [moment(checkInDate), moment(checkOutDate)]
              : undefined
          }
          onChange={(dates, dateStrings) =>
            onChangeDate(dateStrings as string[])
          }
          disabledDate={(current) => moment().add(-1, "days") >= current}
          style={{
            borderRadius: "6px",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
          }}
        />
        {/* @ts-ignore */}
        <Dropdown
          visible={visibleDropdown}
          onVisibleChange={onVisibleDropdown}
          className="dropdownSearch"
          trigger={["hover"]}
          overlay={menu}
          placement="bottomRight"
          arrow
        >
          <Button
            size="large"
            style={{
              borderRadius: "6px",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              color: "#6c757d",
              border: "1px solid #d9d9d9",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#e90056";
              e.currentTarget.style.color = "#e90056";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#d9d9d9";
              e.currentTarget.style.color = "#6c757d";
            }}
          >
            {adultPax} Adult &nbsp;·&nbsp; {childPax} Child &nbsp;·&nbsp;{" "}
            {roomQty} Room
          </Button>
        </Dropdown>

        <Button
          className="buttonSearch"
          loading={searching}
          style={{
            backgroundColor: "#e90056",
            borderColor: "#e90056",
            borderRadius: "6px",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            fontWeight: 600,
            transition: "all 0.3s ease",
          }}
          size="large"
          type="primary"
          onClick={onSearch}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 4px 12px rgba(233, 0, 86, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          Search
        </Button>
      </div>
    </div>
  );
};

// Room Pricing Component with Special Rate Selection
const RoomPricing = ({
  item,
  roomQty,
  totalNights,
  adultPax,
  childPax,
  onSelectRoom,
  totalPax,
}: {
  item: ExtendedRoom;
  roomQty: number;
  totalNights: number;
  adultPax: number;
  childPax: number;
  onSelectRoom: (
    room: ExtendedRoom,
    priceType: number,
    roomQty: number,
    totalPax: number,
    rate: {
      type: "standard" | "rateplan";
      rateplan?: RatePlan;
      specialRate?: SpecialRate;
    }
  ) => void;
  totalPax: number;
}) => {
  const originalPrice = parseFloat(
    item.RoomPrice?.per_room_OriginalPrice || "0"
  );
  const discountPrice = parseFloat(
    item.RoomPrice?.DiscountPrice?.toString() || "0"
  );
  const discountType = item.RoomPrice?.DiscountType || "";

  // Extract discount rate from DiscountType
  const extractDiscountRate = (discountType: string): number => {
    if (!discountType) return 0;
    const match = discountType.match(/DISCOUNT\s+([\d.]+)/i);
    if (match && match[1]) {
      return parseFloat(match[1]) * 100;
    }
    return 0;
  };

  const discountRateFromType = extractDiscountRate(discountType);
  const discountRate =
    parseFloat(item.RoomPrice?.DiscountRate?.toString() || "0") ||
    discountRateFromType;

  const getStandardPrice = () => {
    if (discountPrice > 0) {
      return discountPrice;
    }
    if (discountRate > 0) {
      return originalPrice - (originalPrice * discountRate) / 100;
    }
    return originalPrice;
  };

  const standardRatePrice = getStandardPrice();
  const ratePlans = Array.isArray(item.RoomPrice?.rateplan)
    ? item.RoomPrice.rateplan
    : [];

  return (
    <div>
      {/* Show EITHER standard price card OR rateplans, not both */}
      {/* {ratePlans.length === 0 ? ( */}
      {/* // Original/Standard Price Card - Show only if NO rateplans */}
      <div
        style={{
          backgroundColor: "#e8e8e8",
          padding: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            {originalPrice > standardRatePrice && (
              <Tag
                color="#ff4d4f"
                style={{ fontSize: "12px", fontWeight: "bold" }}
              >
                {discountType}
              </Tag>
            )}
            <Tag color="#2db7f5">Full payment upfront</Tag>
            <Tag color="#87d068">Non-refundable</Tag>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 12,
              marginBottom: 8,
              flexWrap: "wrap",
            }}
          >
            {originalPrice > standardRatePrice && (
              <Text
                style={{
                  textDecoration: "line-through",
                  color: "#8c8c8c",
                  fontSize: "18px",
                  fontWeight: 500,
                }}
              >
                RM {originalPrice.toFixed(2)}
              </Text>
            )}
            <Title
              level={4}
              style={{
                margin: 0,
                color: "#000",
              }}
            >
              RM <b>{standardRatePrice.toFixed(2)}</b>
            </Title>
            {originalPrice > standardRatePrice && (
              <Text
                style={{ color: "blue", fontSize: "14px", fontWeight: 600 }}
              >
                Save RM {(originalPrice - standardRatePrice).toFixed(2)}
              </Text>
            )}
          </div>

          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              RM {standardRatePrice.toFixed(2)} per night
            </Text>
          </div>

          <Text
            style={{
              fontSize: "12px",
              color: "#595959",
              marginTop: 4,
              display: "block",
            }}
          >
            for {roomQty} Room{roomQty > 1 ? "s" : ""} - {totalNights} Night
            {totalNights > 1 ? "s" : ""} - {adultPax} Adult
            {adultPax > 1 ? "s" : ""}{" "}
            {childPax > 0
              ? `and ${childPax} Child${childPax > 1 ? "s" : ""}`
              : ""}
          </Text>
        </div>
        <Button
          style={{
            backgroundColor: "#e90056",
            borderColor: "#e90056",
            marginLeft: 16,
          }}
          size="large"
          type="primary"
          onClick={() =>
            onSelectRoom(item, 0, roomQty, totalPax, { type: "standard" })
          }
        >
          Select
        </Button>
      </div>
      {/* ) : ( */}
      {/* // Rateplan Options - Show ONLY if rateplans exist */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {ratePlans.map((rp) => (
          <div
            key={rp.id}
            style={{
              backgroundColor: "#e8e8e8",
              padding: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <div style={{ flex: 1 }}>
              {/* Rateplan Title - Emphasized */}
              <div
                style={{
                  marginBottom: 12,
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#000",
                }}
              >
                {rp.title}
              </div>

              {/* Badges Row */}
              <div
                style={{
                  marginBottom: 12,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                {!rp.refundable && (
                  <Tag color="#ff4d4f" style={{ fontSize: "12px" }}>
                    Non-refundable
                  </Tag>
                )}
                {rp.refundable && (
                  <Tag color="green" style={{ fontSize: "12px" }}>
                    Free cancellation
                  </Tag>
                )}
                <Tag color="#2db7f5" style={{ fontSize: "12px" }}>
                  Full payment upfront
                </Tag>
                {rp.pay_later && (
                  <Tag color="blue" style={{ fontSize: "12px" }}>
                    Book now, pay later
                  </Tag>
                )}
                {rp.breakfast && (
                  <Tag color="gold" style={{ fontSize: "12px" }}>
                    Breakfast included
                  </Tag>
                )}
              </div>

              {/* Price Display */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 12,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    color: "#000",
                  }}
                >
                  RM <b>{parseFloat(rp.amount).toFixed(2)}</b>
                </Title>
              </div>

              {/* Price Per Night */}
              <div style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  RM {parseFloat(rp.amount).toFixed(2)} per night
                </Text>
              </div>

              {/* Booking Details */}
              <Text
                style={{
                  fontSize: "12px",
                  color: "#595959",
                  display: "block",
                }}
              >
                for {roomQty} Room{roomQty > 1 ? "s" : ""} - {totalNights} Night
                {totalNights > 1 ? "s" : ""} - {adultPax} Adult
                {adultPax > 1 ? "s" : ""}{" "}
                {childPax > 0
                  ? `and ${childPax} Child${childPax > 1 ? "s" : ""}`
                  : ""}
              </Text>
            </div>
            <Button
              style={{
                backgroundColor: "#e90056",
                borderColor: "#e90056",
                marginLeft: 16,
              }}
              size="large"
              type="primary"
              onClick={() =>
                onSelectRoom(item, 0, roomQty, totalPax, {
                  type: "rateplan",
                  rateplan: rp,
                })
              }
            >
              Select
            </Button>
          </div>
        ))}
      </div>
      {/* )} */}

      {/* Special Rates Dropdown - Always Available */}
      {Array.isArray(item.RoomPrice?.special) &&
        item.RoomPrice.special.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Text
              style={{
                fontSize: "13px",
                color: "#666",
                display: "block",
                marginBottom: 8,
                fontWeight: 600,
              }}
            >
              Special Rates (Optional)
            </Text>
            <Select
              placeholder="Choose a special rate"
              style={{
                width: "100%",
              }}
              allowClear
              dropdownStyle={{ minWidth: 250 }}
              onChange={(value) => {
                if (value) {
                  const selectedSpecialRate = (
                    item.RoomPrice.special as SpecialRate[]
                  ).find((rate) => rate.id === value);
                  if (selectedSpecialRate) {
                    onSelectRoom(item, 0, roomQty, totalPax, {
                      type: "rateplan",
                      specialRate: selectedSpecialRate,
                    });
                  }
                }
              }}
              optionLabelProp="label"
            >
              {(item.RoomPrice.special as SpecialRate[]).map((rate) => (
                <Select.Option
                  key={rate.id}
                  value={rate.id}
                  label={
                    <div>
                      <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                        {rate.title}
                      </div>
                      <div style={{ fontSize: "11px", color: "#1890ff" }}>
                        RM {parseFloat(rate.amount).toFixed(2)}
                      </div>
                    </div>
                  }
                >
                  <div style={{ padding: "4px 0" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        marginBottom: "2px",
                      }}
                    >
                      {rate.title}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginBottom: "4px",
                      }}
                    >
                      Special rate
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#1890ff",
                        fontWeight: "bold",
                      }}
                    >
                      RM {parseFloat(rate.amount).toFixed(2)}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </div>
        )}
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="productListWrapper">
    {[1, 2, 3].map((item) => (
      <Card
        key={item}
        style={{
          marginBottom: 20,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
        loading={true}
      >
        <div style={{ display: "flex" }}>
          <div style={{ width: "40%", marginRight: 20 }}>
            <Skeleton.Image
              // @ts-ignore
              active
              style={{
                width: "100%",
                height: 220,
                borderRadius: 6,
              }}
            />
            <Row gutter={[10, 0]} style={{ marginTop: 5 }}>
              {[1, 2, 3].map((img) => (
                <Col key={img} span={8}>
                  <Skeleton.Image
                    // @ts-ignore
                    active
                    style={{
                      width: "100%",
                      height: 90,
                      borderRadius: 4,
                    }}
                  />
                </Col>
              ))}
            </Row>
          </div>
          <div style={{ width: "60%" }}>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        </div>
      </Card>
    ))}
  </div>
);

const RoomListingView = ({
  state,
  actions,
}: {
  state: ReturnType<typeof useBookingState>;
  actions: ReturnType<typeof useBookingActions>;
}) => {
  const {
    rooms,
    checkInDate,
    checkOutDate,
    adultPax,
    childPax,
    roomQty,
    initialLoading,
    searching,
  } = state;

  const { onSelectRoom, onShowAllAmenities, onOpenPriceSummaryModal } = actions;

  // Show loading skeleton during initial load or search
  if (initialLoading || searching) {
    return <LoadingSkeleton />;
  }

  // Show empty state when no rooms
  if (rooms.length === 0 && !initialLoading && !searching) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 20px",
          backgroundColor: "#fafafa",
          borderRadius: 8,
          marginTop: 20,
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 16 }}>🏨</div>
        <Title level={4} style={{ color: "#6c757d", marginBottom: 8 }}>
          Search Room
        </Title>
        <Text type="secondary" style={{ fontSize: 16 }}>
          Please select your check-in and check-out dates to see available
          rooms.
        </Text>
        <br />
        {/* {checkInDate && checkOutDate && (
          <Button
            type="primary"
            style={{
              backgroundColor: "#e90056",
              borderColor: "#e90056",
              marginTop: 16,
            }}
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        )} */}
      </div>
    );
  }

  const IconText = ({
    icon,
    text,
    iconProps,
  }: {
    icon: React.ComponentType<any>;
    text: string;
    iconProps?: any;
  }) => (
    <Space>
      {React.createElement(icon, iconProps)}
      {text}
    </Space>
  );

  const renderRoomItem = (item: ExtendedRoom) => {
    const totalNights = totalDaysBetweenTwoDates(checkInDate, checkOutDate);
    const totalPax = adultPax + childPax;

    return (
      <List.Item>
        <List.Item.Meta
          title={
            <div>
              <Title level={4} style={{ color: "#000" }}>
                {item.RoomName}
              </Title>
            </div>
          }
        />
        <div className="roomListWrapper">
          <div style={{ width: "100%", marginRight: 20 }}>
            <Row>
              <Col span={24}>
                <Image
                  width={"100%"}
                  height={220}
                  src={item.RoomPicture[0]}
                  preview={false}
                />
              </Col>
            </Row>
            <Row gutter={[10, 0]} style={{ display: "flex", marginTop: 5 }}>
              {item.RoomPicture.slice(1, 4).map((image, imageIndex) => (
                <Col key={imageIndex} span={8}>
                  <Image
                    width={"100%"}
                    height={90}
                    src={image}
                    preview={false}
                  />
                </Col>
              ))}
            </Row>
          </div>
          <div style={{ width: "100%" }}>
            <div className="roomSummaryWrapper">
              <Text>
                <span
                  dangerouslySetInnerHTML={{ __html: item.RoomDescription }}
                />
              </Text>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <Text>
                <b>Amenities</b>
              </Text>
              <div style={{ marginTop: ".5em" }}>
                {item?.Amenities?.slice(
                  0,
                  item.Amenities?.length > 10 ? 10 : item.Amenities?.length
                ).map((ame, ameIndex) => {
                  const Icon = iconMap[ame?.roomfacilities_pic];
                  return (
                    <div
                      key={ameIndex}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: "6px",
                      }}
                    >
                      {Icon ? (
                        <Icon style={{ marginRight: 8 }} />
                      ) : (
                        <span style={{ marginRight: 8 }}>❓</span>
                      )}
                      <span>{ame?.roomfacilities}</span>
                    </div>
                  );
                })}
              </div>
              {item?.Amenities?.length > 10 && (
                <Link onClick={() => onShowAllAmenities(item?.Amenities)}>
                  See all amenities
                </Link>
              )}
            </div>
          </div>
        </div>

        {parseFloat(item.RoomPrice?.total_OriginalPrice?.toString() || "0") !==
        0.0 ? (
          <RoomPricing
            item={item}
            roomQty={roomQty}
            totalNights={totalNights}
            adultPax={adultPax}
            childPax={childPax}
            // checkInDate={checkInDate}
            onSelectRoom={onSelectRoom}
            // onOpenPriceSummaryModal={onOpenPriceSummaryModal}
            totalPax={totalPax}
          />
        ) : (
          <div
            style={{
              backgroundColor: "#e8e8e8",
              padding: 10,
              marginTop: "10px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <Text>Room not available at the moment</Text>
            </div>
          </div>
        )}
      </List.Item>
    );
  };

  return (
    <div className="productListWrapper">
      <List
        itemLayout="vertical"
        size="large"
        dataSource={rooms}
        locale={{
          emptyText: "Please search any dates to see the room availability",
        }}
        renderItem={renderRoomItem}
      />
    </div>
  );
};

// Price Summary Modal
const PriceSummaryModal = ({
  visible,
  onClose,
  data,
  checkInDate,
  checkOutDate,
  roomQty,
}: {
  visible: boolean;
  onClose: () => void;
  data: any;
  checkInDate: string;
  checkOutDate: string;
  roomQty: number;
}) => {
  if (!data) return null;

  const totalNights = totalDaysBetweenTwoDates(checkInDate, checkOutDate);
  const prices = Array.from({ length: roomQty }, (_, i) => i + 1);
  const selectedRate = data?.price?.selectedRate || { type: "standard" };

  return (
    // @ts-ignore
    <Modal
      title="Price Summary"
      visible={visible}
      footer={null}
      onCancel={onClose}
    >
      <div>
        {selectedRate.type === "standard" ? (
          <Alert
            message={`Room rate at discounted rate of RM ${parseFloat(
              data?.price.per || 0
            ).toFixed(2)} per night`}
            type="info"
            showIcon
          />
        ) : (
          <Alert
            message={`Room rate at ${
              selectedRate.specialRate?.title || selectedRate.rateplan?.title
            } of RM ${parseFloat(data?.price.per || 0).toFixed(2)} per night`}
            type="info"
            showIcon
          />
        )}

        <div style={{ marginTop: 20 }}>
          {prices.map((_, priceIndex) => {
            const originalPrice = parseFloat(
              data.RoomPrice?.per_room_OriginalPrice || "0"
            );
            const currentPrice = parseFloat(data?.price.per || 0);
            const hasDiscount =
              currentPrice < originalPrice && originalPrice > 0;

            return (
              <div
                key={priceIndex}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginBottom: 8,
                }}
              >
                <div>
                  <Text>
                    Room {priceIndex + 1} -{" "}
                    {hasDiscount && (
                      <Text
                        style={{
                          textDecoration: "line-through",
                          color: "#8c8c8c",
                          fontSize: "13px",
                          marginRight: 8,
                        }}
                      >
                        RM {originalPrice.toFixed(2)}
                      </Text>
                    )}
                    <Text strong style={{ color: "#e90056" }}>
                      RM {currentPrice.toFixed(2)}
                    </Text>{" "}
                    x {totalNights} Night{totalNights > 1 ? "s" : ""}
                  </Text>
                </div>
                <div style={{ textAlign: "right" }}>
                  {hasDiscount && (
                    <Text
                      style={{
                        textDecoration: "line-through",
                        color: "#8c8c8c",
                        fontSize: "13px",
                        display: "block",
                        marginBottom: 2,
                      }}
                    >
                      RM {(originalPrice * totalNights).toFixed(2)}
                    </Text>
                  )}
                  <Text strong style={{ color: "#e90056" }}>
                    RM {(currentPrice * totalNights).toFixed(2)}
                  </Text>
                </div>
              </div>
            );
          })}

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <Text>Sub Total</Text>
            <div style={{ textAlign: "right" }}>
              {(() => {
                const originalTotal =
                  parseFloat(data.RoomPrice?.per_room_OriginalPrice || "0") *
                  totalNights *
                  roomQty;
                const currentTotal = parseFloat(data?.price.total || 0);
                const hasDiscount =
                  currentTotal < originalTotal && originalTotal > 0;

                return (
                  <>
                    {hasDiscount && (
                      <Text
                        style={{
                          textDecoration: "line-through",
                          color: "#8c8c8c",
                          fontSize: "13px",
                          display: "block",
                          marginBottom: 2,
                        }}
                      >
                        RM {originalTotal.toFixed(2)}
                      </Text>
                    )}
                    <Text strong style={{ color: "#e90056" }}>
                      RM {currentTotal.toFixed(2)}
                    </Text>
                  </>
                );
              })()}
            </div>
          </div>

          {(() => {
            const originalPrice = parseFloat(
              data.RoomPrice?.per_room_OriginalPrice || "0"
            );
            const currentPrice = parseFloat(data?.price.per || 0);
            const discountType = data.RoomPrice?.DiscountType || "";

            // Calculate actual percentage off based on price difference
            const calculatePercentageOff = (
              original: number,
              current: number
            ): number => {
              if (original <= 0 || current >= original) return 0;
              return Math.round(((original - current) / original) * 100);
            };

            const actualPercentageOff = calculatePercentageOff(
              originalPrice,
              currentPrice
            );
            const discountAmount =
              (originalPrice - currentPrice) * totalNights * roomQty;

            if (actualPercentageOff > 0 && discountAmount > 0) {
              const discountLabel = discountType
                ? `${discountType} (${actualPercentageOff}%)`
                : `Discount (${actualPercentageOff}%)`;

              return (
                <>
                  <Divider style={{ marginTop: 10, marginBottom: 10 }} />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text>{discountLabel}</Text>
                    <Text>- RM {discountAmount.toFixed(2)}</Text>
                  </div>
                </>
              );
            }
            return null;
          })()}

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>
              <b>Total</b>
            </Text>
            <Text>
              <b>RM {parseFloat(data?.price.total || 0).toFixed(2)}</b>
            </Text>
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Main Component
const RoomList = observer(() => {
  const state = useBookingState();
  const actions = useBookingActions(state);

  const {
    visibleAmenitiesModal,
    selectedAmenities,
    priceSummaryModalVisible,
    priceSummaryModalData,
    checkInDate,
    checkOutDate,
    roomQty,
  } = state;

  const { setVisibleAmenitiesModal, setPriceSummaryModalVisible } = state;

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar />

        <div style={{ minHeight: 500 }}>
          <SearchSection state={state} actions={actions} />
          <RoomListingView state={state} actions={actions} />
        </div>

        <Footer />
      </div>

      {/* Modals */}
      <PriceSummaryModal
        visible={priceSummaryModalVisible}
        onClose={() => setPriceSummaryModalVisible(false)}
        data={priceSummaryModalData}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        roomQty={roomQty}
      />
      {/* @ts-ignore */}
      <Modal
        title="Amenities"
        visible={visibleAmenitiesModal}
        footer={null}
        onCancel={() => setVisibleAmenitiesModal(false)}
      >
        {selectedAmenities?.map((ame, ameIndex) => {
          const Icon = iconMap[ame?.roomfacilities_pic];
          return (
            <div
              key={ameIndex}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              {Icon ? (
                <Icon style={{ marginRight: 8 }} />
              ) : (
                <span style={{ marginRight: 8 }}>❓</span>
              )}
              <span>{ame?.roomfacilities}</span>
            </div>
          );
        })}
      </Modal>
    </div>
  );
});

export default RoomList;
